// Import the combineReducers function from redux to merge multiple reducers
import { combineReducers } from 'redux'

// Import the cart reducer and also all helper selectors (using a namespace) from the cart file
import cart, * as fromCart from './cart'

// Import the products reducer and also all helper selectors from the products file
import products, * as fromProducts from './products'

import discountCodesReducer from './discountCode';


// Import additional reducers for authentication, orders, and categories
import auth from "./login";
import orders from "./order";
import categories from "./categories";

// Combine all the reducers into one root reducer.
// The keys (cart, products, auth, orders, categories) will represent the corresponding state slices.
export default combineReducers({
    cart,
    products,
    auth,
    orders,
    categories,
    discountCodes: discountCodesReducer
});

// ------------------
// Selectors Section
// ------------------

// getAddedIds: Returns an array of product IDs that have been added to the cart.
// It uses the helper function from the cart reducer.
const getAddedIds = state => fromCart.getAddedIds(state.cart);

// getQuantity: Returns the quantity for a specific product ID in the cart.
// It uses the helper function from the cart reducer.
const getQuantity = (state, id) => fromCart.getQuantity(state.cart, id);

// getProduct: Retrieves the product details for a given product ID from the products state.
// It uses the helper function from the products reducer.
const getProduct = (state, id) => fromProducts.getProduct(state.products, id);

export const getAllDiscountCodes = (state) => state.discountCodes.codes;

// ---------------------------
// Calculation of Totals
// ---------------------------

// getTotal: Calculates the total price of the items in the cart after applying product-level discounts.
// It multiplies each product's price (after discount) by its quantity and sums them up.
export const getTotalCartAfterProductDiscount = state => {
    // Check if products are loaded; if not, return 0.
    if (state.products && state.products.byId && Object.keys(state.products.byId).length === 0) {
        return 0; // Return an initial total if products are not available.
    }
    return getAddedIds(state)
        .reduce((total, id) =>
                total + getProduct(state, id).price * (1 - getProduct(state, id).discount) * getQuantity(state, id),
            0
        )
        .toFixed(2); // Format the total to 2 decimal places.
}

// getTotalWithDiscountCode: Calculates the total price after applying a discount code.
// It first calculates the total price (like getTotal) and then applies the cart-level discount.
export const getTotalCartWithDiscountCode = state => {
    // Get the discount from the cart state; default to 0 if none is set.
    const discount = state.cart.discount || 0;
    //console.log("discount in reducer", discount);

    // Check if products are loaded; if not, return 0.
    if (state.products && state.products.byId && Object.keys(state.products.byId).length === 0) {
        return 0;
    }

    // Calculate total without discount code.
    const totalWithoutDiscountCode = getAddedIds(state)
        .reduce((total, id) =>
                total + getProduct(state, id).price * (1 - getProduct(state, id).discount) * getQuantity(state, id),
            0
        )
        .toFixed(2);
    // Apply the discount code: reduce the total by the discount percentage.
    return totalWithoutDiscountCode * (1 - discount);
};

// getTotalFullPrice: Calculates the total price without any discounts applied.
// It multiplies each product's full price by its quantity.
export const getTotalFullPrice = state => {
    if (state.products && state.products.byId && Object.keys(state.products.byId).length === 0) {
        return 0;
    }
    return getAddedIds(state)
        .reduce((total, id) =>
                total + getProduct(state, id).price * getQuantity(state, id),
            0
        )
        .toFixed(2);
};

// -----------------------------
// Cart Products Selector
// -----------------------------

// getCartProducts: Returns an array of product objects that are in the cart,
// adding the quantity from the cart state to each product.
export const getCartProducts = state =>
    getAddedIds(state).map(id => ({
        ...getProduct(state, id),  // Get all product details for the id
        quantity: getQuantity(state, id) // Add the quantity from the cart state
    }));
